import { LoadingButton } from '@mui/lab';
import { Alert, Button, Collapse, Divider, Grid, TextField } from '@mui/material';
import { FormikValues } from 'formik';
import { get, isString } from 'lodash';
import { ComponentType, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { State } from 'src/common/store';
// import { OrganizationsSelect } from 'src/components/organizations';
import { FIELD_REGION } from 'src/components/districts/districts-edit-form/districts-edit-schema';
import { RegionsSelect } from 'src/components/regions';
import { useAuth, useEdit } from '../../../hooks';
import {
  clearUser,
  clearUserRequest,
  createUser,
  editUser,
  getUser,
  getUserByPin,
} from '../../../thunks/user-thunks';
import { RolesSelect } from '../../roles';
import {
  FIELD_EMAIL,
  FIELD_FIRST_NAME,
  FIELD_LAST_NAME,
  FIELD_LOCATION,
  FIELD_MIDDLE_NAME,
  FIELD_ORGANIZATION,
  FIELD_PASSPORT_NUMBER,
  FIELD_PASSPORT_SERIES,
  FIELD_PHONE,
  FIELD_PIN,
  FIELD_ROLE,
  UsersEditValidationForm,
} from './users-edit-schema';

export interface UserEditFormProps {
  id?: string | number;
  onCancel?: () => void;
  onSubmit: (values: FormikValues) => void;
}

export const UsersEditForm: ComponentType<UserEditFormProps> = ({ id, onCancel, onSubmit }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isSuperAdmin, user } = useAuth();
  const { isLoading, isFetching, error, formik, handleFieldChange, hasError, getErrorMessage } =
    useEdit({
      id,
      entity: 'user',
      initialValues: {
        [FIELD_PIN]: '',
        [FIELD_PASSPORT_SERIES]: '',
        [FIELD_PASSPORT_NUMBER]: '',
        [FIELD_FIRST_NAME]: '',
        [FIELD_LAST_NAME]: '',
        [FIELD_MIDDLE_NAME]: '',
        [FIELD_PHONE]: '',
        [FIELD_EMAIL]: '',
        [FIELD_ORGANIZATION]: get(user, ['data', 'organization', 'id'], ''),
        [FIELD_ROLE]: '',
        [FIELD_LOCATION]: '',
      },
      validationSchema: UsersEditValidationForm,
      onGetOne: getUser,
      onClearOne: clearUser,
      onClear: clearUserRequest,
      onCreate: createUser,
      onEdit: editUser,
      onSubmit: onSubmit,
    });

  const { error: oneUserError } = useSelector((state: State) => state.user.one);

  useEffect(() => {
    if (oneUserError) formik.setFieldError([FIELD_PIN], oneUserError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oneUserError]);

  const pin = get(formik, ['values', FIELD_PIN], null);

  const handleUserByPin = () => {
    formik.validateField(FIELD_PIN).then((res: any) => {
      const pinError = get(formik, ['errors', FIELD_PIN]);
      if (pinError) {
        formik.setFieldTouched(FIELD_PIN, true, true);
      } else {
        dispatch(getUserByPin(pin));
      }
    });
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container mb={3} columnSpacing={3}>
        <Grid item xs={7}>
          <TextField
            {...formik.getFieldProps(FIELD_PIN)}
            fullWidth
            helperText={getErrorMessage(FIELD_PIN)}
            error={hasError(FIELD_PIN)}
            label={t('profile.pin', 'ПИНФЛ')}
            placeholder={t('profile.pin.placeholder', 'Введите ПИНФЛ')}
            onChange={(e: any) => handleFieldChange(FIELD_PIN, e)}
            disabled={isLoading || isFetching || !!id}
            inputProps={{ maxLength: 14 }}
          />
        </Grid>
        <Grid item xs={5}>
          <Button
            onClick={handleUserByPin}
            variant="contained"
            color="primary"
            size="large"
            disabled={!pin || isLoading || isFetching || !!id}
            fullWidth
            sx={{ mt: 3.125 }}
          >
            {t('action.search.by.pin', 'Найти по ПИНФЛ')}
          </Button>
        </Grid>
        <Grid item xs={5}>
          <TextField
            {...formik.getFieldProps(FIELD_PASSPORT_SERIES)}
            onChange={(e: any) => handleFieldChange(FIELD_PASSPORT_SERIES, e)}
            error={hasError(FIELD_PASSPORT_SERIES)}
            helperText={getErrorMessage(FIELD_PASSPORT_SERIES)}
            label={t('profile.passportSeria', 'Серия паспорта')}
            placeholder={t('profile.passportSeria.placeholder', 'Введите серию паспорта')}
            fullWidth
            disabled
          />
        </Grid>
        <Grid item xs={7}>
          <TextField
            {...formik.getFieldProps(FIELD_PASSPORT_NUMBER)}
            onChange={(e: any) => handleFieldChange(FIELD_PASSPORT_NUMBER, e)}
            error={hasError(FIELD_PASSPORT_NUMBER)}
            helperText={getErrorMessage(FIELD_PASSPORT_NUMBER)}
            label={t('profile.passportNumber', 'Номер паспорта')}
            placeholder={t('profile.passportNumber.placeholder', 'Введите номер паспорта')}
            fullWidth
            disabled
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            {...formik.getFieldProps(FIELD_LAST_NAME)}
            onChange={(e: any) => handleFieldChange(FIELD_LAST_NAME, e)}
            error={hasError(FIELD_LAST_NAME)}
            helperText={getErrorMessage(FIELD_LAST_NAME)}
            label={t('profile.lastName', 'Фамилия')}
            placeholder={t('profile.lastName.placeholder', 'Введите фамилию')}
            fullWidth
            disabled
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            {...formik.getFieldProps(FIELD_FIRST_NAME)}
            onChange={(e: any) => handleFieldChange(FIELD_FIRST_NAME, e)}
            error={hasError(FIELD_FIRST_NAME)}
            helperText={getErrorMessage(FIELD_FIRST_NAME)}
            label={t('profile.firstName', 'Имя')}
            placeholder={t('profile.firstName.placeholder', 'Введите имя')}
            fullWidth
            disabled
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            {...formik.getFieldProps(FIELD_MIDDLE_NAME)}
            onChange={(e: any) => handleFieldChange(FIELD_MIDDLE_NAME, e)}
            error={hasError(FIELD_MIDDLE_NAME)}
            helperText={getErrorMessage(FIELD_MIDDLE_NAME)}
            label={t('profile.middleName', 'Отчество')}
            placeholder={t('profile.middleName.placeholder', 'Введите отчество')}
            fullWidth
            disabled
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            {...formik.getFieldProps(FIELD_PHONE)}
            onChange={(e: any) => handleFieldChange(FIELD_PHONE, e)}
            error={hasError(FIELD_PHONE)}
            helperText={getErrorMessage(FIELD_PHONE)}
            label={t('profile.phone', 'Номер телефона')}
            placeholder={t('profile.phone.placeholder', 'Введите номер телефона')}
            fullWidth
            disabled
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            {...formik.getFieldProps(FIELD_EMAIL)}
            onChange={(e: any) => handleFieldChange(FIELD_EMAIL, e)}
            error={hasError(FIELD_EMAIL)}
            helperText={getErrorMessage(FIELD_EMAIL)}
            label={t('profile.email', 'Электронная почта')}
            placeholder={t('profile.email.placeholder', 'Введите электронную почту')}
            fullWidth
            disabled
          />
        </Grid>
        {isSuperAdmin && (
          <Grid item xs={6}>
            <RegionsSelect
              {...formik.getFieldProps(FIELD_REGION)}
              onChange={(e: any) => handleFieldChange(FIELD_REGION, e)}
              error={hasError(FIELD_REGION)}
              helperText={getErrorMessage(FIELD_REGION)}
              label={t('region', 'Регион')}
              placeholder={t('region.placeholder', 'Выберите регион')}
              fullWidth
              disabled={isLoading || isFetching}
              organization={formik.values.region}
            />
            {/* <OrganizationsSelect
              {...formik.getFieldProps(FIELD_ORGANIZATION)}
              onChange={(e: any) => handleFieldChange(FIELD_ORGANIZATION, e)}
              error={hasError(FIELD_ORGANIZATION)}
              helperText={getErrorMessage(FIELD_ORGANIZATION)}
              label={t('profile.organization', 'Организация')}
              placeholder={t('profile.organization.placeholder', 'Выберите организацию')}
              fullWidth
              disabled={isLoading || isFetching}
            /> */}
          </Grid>
        )}
        <Grid item xs={isSuperAdmin ? 6 : 12}>
          <RolesSelect
            {...formik.getFieldProps(FIELD_ROLE)}
            onChange={(e: any) => handleFieldChange(FIELD_ROLE, e)}
            error={hasError(FIELD_ROLE)}
            helperText={getErrorMessage(FIELD_ROLE)}
            label={t('profile.role', 'Роль')}
            placeholder={t('profile.role.placeholder', 'Выберите роль')}
            fullWidth
            disabled={isLoading || isFetching}
          />
        </Grid>
        {/* <Grid item xs={12} component={Collapse} in={formik.values[FIELD_ROLE] === Roles.INSPECTOR}>
          <LocationsSelect
            {...formik.getFieldProps(FIELD_LOCATION)}
            onChange={(e: any) => handleFieldChange(FIELD_LOCATION, e)}
            error={hasError(FIELD_LOCATION)}
            helperText={getErrorMessage(FIELD_LOCATION)}
            label={t('location', 'Офис')}
            placeholder={t('location.placeholder', 'Выберите офис')}
            fullWidth
            disabled={isLoading || isFetching}
            organization={formik.values.organization}
          />
        </Grid> */}
      </Grid>
      {isString(error) && (
        <Collapse in={!!error}>
          <Alert severity="error">{error}</Alert>
        </Collapse>
      )}
      <Divider sx={{ mt: 3, mb: 4 }} />
      <Grid container columnSpacing={2} justifyContent="space-between">
        <Grid item>
          {onCancel && (
            <Button onClick={onCancel} variant="outlined" size="large" disabled={isLoading}>
              {t('cancel', 'Отмена')}
            </Button>
          )}
        </Grid>
        <Grid item>
          <LoadingButton
            loading={isLoading || isFetching}
            type="submit"
            variant="contained"
            color="secondary"
            size="large"
          >
            {t('action.save', 'Сохранить')}
          </LoadingButton>
        </Grid>
      </Grid>
    </form>
  );
};
